import { useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import GrIcon from "./GrIcon";
import icons from "../icons";
import { gr } from "../tokens";

// Photos of a post in one tidy block, laid out as on Facebook (up to 5 shown, the 5th says "+N" when
// there are more): 1 = one 16:9 photo; 2 = two side by side; 3 = one large on top, two below;
// 4 = one large on top, three below; 5+ = two on top, three below. Every photo is cropped to its tile
// (no layout shift), lazy-loaded, opens with `onOpen(index)`, and a photo that fails to load shows an
// icon instead of alt text. Areas are "row-start / column-start / row-end / column-end".
const MAX_SHOWN = 5;
const LAYOUTS = {
  1: { columns: "1fr", rows: "1fr", ratio: "16 / 9", areas: ["1 / 1 / 2 / 2"] },
  2: {
    columns: "1fr 1fr",
    rows: "1fr",
    ratio: "2 / 1",
    areas: ["1 / 1 / 2 / 2", "1 / 2 / 2 / 3"],
  },
  3: {
    columns: "1fr 1fr",
    rows: "2fr 1fr",
    ratio: "4 / 3",
    areas: ["1 / 1 / 2 / 3", "2 / 1 / 3 / 2", "2 / 2 / 3 / 3"],
  },
  4: {
    columns: "1fr 1fr 1fr",
    rows: "2fr 1fr",
    ratio: "4 / 3",
    areas: ["1 / 1 / 2 / 4", "2 / 1 / 3 / 2", "2 / 2 / 3 / 3", "2 / 3 / 3 / 4"],
  },
  5: {
    columns: "repeat(6, 1fr)",
    rows: "3fr 2fr",
    ratio: "6 / 5",
    areas: [
      "1 / 1 / 2 / 4",
      "1 / 4 / 2 / 7",
      "2 / 1 / 3 / 3",
      "2 / 3 / 3 / 5",
      "2 / 5 / 3 / 7",
    ],
  },
};

function Tile({ src, alt, area, more, onClick }) {
  const [failed, setFailed] = useState(false);
  return (
    <ButtonBase
      onClick={onClick}
      aria-label={more ? `${alt}, ${more} more` : alt}
      sx={{
        gridArea: area,
        position: "relative",
        overflow: "hidden",
        background: gr.color.tint,
        color: gr.color.brand,
        "&.Mui-focusVisible": { boxShadow: gr.shadow.focus, zIndex: 1 },
      }}
    >
      {failed ? (
        <GrIcon icon={icons.image} />
      ) : (
        <Box
          component="img"
          src={src}
          alt=""
          loading="lazy"
          onError={() => setFailed(true)}
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
      )}
      {more ? (
        <Box
          aria-hidden="true"
          sx={{
            position: "absolute",
            inset: 0,
            display: "grid",
            placeItems: "center",
            background: gr.color.overlay,
            color: gr.color.onBrand,
            fontSize: gr.font.size.xxl,
            fontWeight: gr.font.weight.semibold,
          }}
        >
          {`+${more}`}
        </Box>
      ) : null}
    </ButtonBase>
  );
}

export default function GrPhotoGrid({
  photos = [],
  alt = "Photo",
  onOpen,
  sx,
}) {
  if (!photos.length) return null;
  const shown = photos.slice(0, MAX_SHOWN);
  const layout = LAYOUTS[shown.length];
  const more = photos.length - shown.length;
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: layout.columns,
        gridTemplateRows: layout.rows,
        aspectRatio: layout.ratio,
        gap: gr.space[1],
        borderRadius: gr.radius.tile,
        overflow: "hidden",
        ...sx,
      }}
    >
      {shown.map((src, index) => (
        <Tile
          key={`${src}-${index}`}
          src={src}
          alt={`${alt} ${index + 1} of ${photos.length}`}
          area={layout.areas[index]}
          more={index === shown.length - 1 && more > 0 ? more : 0}
          onClick={(e) => onOpen && onOpen(index, e)}
        />
      ))}
    </Box>
  );
}

GrPhotoGrid.propTypes = {
  photos: PropTypes.arrayOf(PropTypes.string),
  alt: PropTypes.string,
  onOpen: PropTypes.func,
  sx: PropTypes.object,
};
